import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "../../components/ui/ConfirmDialogue";
import countryList from "react-select-country-list";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { useLocation, useNavigate } from "react-router-dom";
import NotFound from "@components/ui/NotFound";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { InputAdornment, TextField } from "@mui/material";
import { useUserData } from "context/OAuth/AuthContext";
import { fields } from "data/dropdowndData/dietPlanPreferenceDropdown";
import { useTranslation } from "react-i18next";
import MedicalViewMultipSelectDropdown from "../../components/ui/customDropdowns/MedicalViewMultipSelectDropdown";
import { createDietPlan, getFields, savePreferences } from "api/dietPlan";

const PreferencesSelection = () => {
  const { subscription } = useUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!location?.state) {
    navigate(-1);
  }
  const isReacreate = location?.state?.isReacreate;
  const [selectedTags, setSelectedTags] = useState({});
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const countries = countryList().getData();

  const countryCodeToName = countries.reduce((acc, country) => {
    acc[country.value] = country.label;
    return acc;
  }, {});
  
  const InputFieldValidation = {
    yourAge: 3,
    height: 3,
    weight: 3,
    waistCircumference: 3,
  };

  const validateInput = (key, value) => {
    const maxLength = InputFieldValidation[key];

    if (!value) {
      return t("preferences.validation.fieldRequired", {
        field: t(`preferences.fields.${key}`),
      });
    }

    if (value < 1) {
      return t("preferences.validation.positive");
    }

    if (isNaN(value)) {
      return t("preferences.validation.invalidNumber");
    }

    if (value < 0) {
      return t("preferences.validation.negative");
    }

    return null;
  };

  const handleClickOpen = () => {
    const newErrors = {};

    fields.forEach(({ key, type }) => {
      if (type === "numeric" && key !== "dailyCalorieAdjustment") {
        const error = validateInput(key, selectedTags[key]);
        if (error) {
          newErrors[key] = error;
        }
      }

      if (
        type === "multi-select" &&
        (!selectedTags[key] || selectedTags[key].length === 0)
      ) {
        newErrors[key] = "Please select at least one option";
      } else if (type === "select" && !selectedTags[key]) {
        newErrors[key] = "Please select an option";
      }
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setOpen(true);
    } else {
      toast.error(t("toast.OopsReview"));
    }
  };

  const handleClose = (key) => {
    if (key === "cancel") {
      setOpen(false);
    } else {
      handleSavePreferences();
      setOpen(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      await savePreferences(selectedTags);
      await createDietPlan();
      toast.info(t("toast.informationSubmit"));
      navigate("/diet-plan");
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast.error(t("toast.failedSubmit"));
    }
  };

  useEffect(() => {
    if (subscription?.status === "active" || subscription?.status === "pending-cancel") {
      getForMFields();
    } else {
      navigate("/renew-subscription");
    }
  }, []);

  const getForMFields = async () => {
    try {
      setIsFieldsLoading(true);
      const response = await getFields();
      const tags = response?.selectedPreferences;
      setSelectedTags(tags);
    } catch (error) {
      console.error(
        "Error fetching diet plan:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setIsFieldsLoading(false);
    }
  };

  const removeTag = (key, tag) => {
    const updated = selectedTags[key].filter((t) => t !== tag);

    if (updated.length === 0) {
      setErrors((prev) => ({ ...prev, [key]: true }));
    }

    setSelectedTags((prev) => ({
      ...prev,
      [key]: updated,
    }));
  };

  const handleSelectChange = (key, value) => {
    setSelectedTags((prev) => ({ ...prev, [key]: value }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!value || value === "") {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const handleInputChange = (key, value) => {
    const error = validateInput(key, value);

    setSelectedTags((prev) => ({ ...prev, [key]: value }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (error) {
        updatedErrors[key] = error;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const handleMultiSelectChange = (key, values) => {
    if (
      key === "healthConditions" ||
      key === "foodAllergies" ||
      key === "exoticDishes"
    ) {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "None") {
        values = ["None"];
      } else {
        values = values.filter((v) => v !== "None");
      }
    }
    if (key === "selectableDays") {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "All Days") {
        values = ["All Days"];
      } else {
        values = values.filter((v) => v !== "All Days");
      }
    }
    setSelectedTags((prev) => ({ ...prev, [key]: values }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!values || values.length === 0) {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  if (location?.state) {
    return (
      <>
        {isFieldsLoading ? (
          <ActualDietPlanLoader />
        ) : (
          <>
            <div className="bg-white shadow-lg p-4 md:p-6 my-4 md:my-6 rounded-lg min-h-[420px] lg:min-h-[400px]">
              {/* Header Section */}
              <div className="text-center mb-6 md:mb-8">
                <h1 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 md:mb-4">
                  {t("preferences.title")}
                </h1>
                <div className="space-y-2 text-sm md:text-base text-gray-600 max-w-3xl mx-auto">
                  <p>{t("preferences.description1")}</p>
                  <p>{t("preferences.description2")}</p>
                  <p>{t("preferences.description3")}</p>
                </div>
              </div>

              {/* Preferences Table */}
              <div className="border border-gray-200 rounded-lg shadow-sm">
                {/* Table Header - Hidden on mobile, shown on desktop */}
                <div className="hidden md:grid bg-gradient-to-r from-blue-600 to-blue-400 text-white p-4 font-semibold">
                  <div className="grid grid-cols-3 gap-4">
                    <span>{t("preferences.headers.options")}</span>
                    <span>{t("preferences.headers.preferences")}</span>
                    <span>{t("preferences.headers.selected")}</span>
                  </div>
                </div>

                {/* Mobile Header */}
                <div className="md:hidden bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white p-3 font-semibold text-center">
                  {t("preferences.Your Preferences")}
                </div>

                {/* Preferences List */}
                <div className="p-3 md:p-4 space-y-4 md:space-y-6">
                  {fields?.length > 0 ? (
                    fields?.map(({ label, key, type, options }) => {
                      if (type !== "country-select") {
                        return (
                          <div
                            key={key}
                            className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            {/* Field Label - Full width on mobile, 1 col on desktop */}
                            <div className="md:col-span-1">
                              <span className="text-sm md:text-base font-medium text-gray-700">
                                {t(`preferences.fields.${key}`)}
                              </span>
                            </div>
                            {/* Input/Dropdown - Full width on mobile, 1 col on desktop */}
                            <div className="md:col-span-1">
                              <div className="w-full">
                                {type === "multi-select" ? (
                                  <MedicalViewMultipSelectDropdown
                                    options={options.map((opt) => ({
                                      value: opt,
                                      label: t(`firstFormSteps.${key}.options.${opt}`),
                                    }))}
                                    selectedValues={selectedTags[key] || []}
                                    onSelect={(selectedOptions) => {
                                      const originalValues = selectedOptions.map(
                                        (opt) =>
                                          typeof opt === "object"
                                            ? opt.value
                                            : opt
                                      );
                                      handleMultiSelectChange(
                                        key,
                                        originalValues
                                      );
                                    }}
                                    hasError={errors[key]}
                                    label={t(`preferences.fields.${key}`)}
                                    placeholder={t(
                                      "preferences.validation.onlyOne"
                                    )}
                                    isEditable={true}
                                    isFistForm={true}
                                  />
                                ) : type === "select" ? (
                                  <>
                                    <select
                                      className={`w-full border p-2 rounded-md h-10 text-sm outline-none ${
                                        errors[key]
                                          ? "border-red-500 text-red-500 bg-white"
                                          : "text-black bg-white"
                                      }`}
                                      value={
                                        Array.isArray(selectedTags[key])
                                          ? selectedTags[key][
                                              selectedTags[key].length - 1
                                            ]
                                          : selectedTags[key] || ""
                                      }
                                      onChange={(e) =>
                                        handleSelectChange(key, e.target.value)
                                      }
                                    >
                                      <option value="" disabled>
                                        {t("preferences.validation.onlyOne")}
                                      </option>
                                      {options.map((option) => (
                                        <option
                                          key={option}
                                          value={option}
                                          className="text-black"
                                        >
                                          {t(
                                            `preferences.options.${key}.${option}`
                                          )}
                                        </option>
                                      ))}
                                    </select>
                                    {errors[key] && (
                                      <div className="text-red-500 text-xs mt-1 ml-1">
                                        {`${t(`preferences.fields.${key}`)} is required`}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  // Numeric input fields
                                  (<>
                                    {label === "Daily Calorie Adjustment (Kcal)" ? (
                                      <input
                                        min="1"
                                        type="number"
                                        className={`w-full border p-2 rounded-md h-10 text-sm focus:outline-none bg-gray-100 cursor-not-allowed`}
                                        disabled
                                        value={
                                          selectedTags["targetDailyCalories"] -
                                            selectedTags["maintenanceCalories"] || "0"
                                        }
                                      />
                                    ) : key === "weight" ? (
                                      <div className="w-full">
                                        <TextField
                                          type="number"
                                          variant="outlined"
                                          fullWidth
                                          size="small"
                                          value={selectedTags.weight || ""}
                                          onChange={(e) =>
                                            handleInputChange(
                                              "weight",
                                              e.target.value,
                                              "Weight"
                                            )
                                          }
                                          onKeyDown={(e) => {
                                            if (
                                              ["e", "E", "+", "-"].includes(
                                                e.key
                                              )
                                            ) {
                                              e.preventDefault();
                                            }
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              fontSize: "0.875rem",
                                              color: "black",
                                              height: "40px",
                                              paddingRight: "5px",
                                              "& fieldset": {
                                                borderColor: errors.weight
                                                  ? "#EF4444"
                                                  : "#e5e7eb",
                                                borderRadius: "0.375rem",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: errors.weight
                                                  ? "#EF4444"
                                                  : "#e5e7eb",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: errors.weight
                                                  ? "#EF4444"
                                                  : "#e5e7eb",
                                                boxShadow: "none",
                                                outline: "none",
                                                borderWidth: "1px",
                                              },
                                              "& input::placeholder": {
                                                fontSize: "0.75rem",
                                                opacity: 1,
                                                color: "#9CA3AF",
                                              },
                                            },
                                          }}
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <div className="flex items-center mr-1">
                                                  {["kg", "Lbs"].map(
                                                    (unit) => (
                                                      <button
                                                        key={unit}
                                                        onClick={() => {
                                                          setSelectedTags(
                                                            (prev) => {
                                                              const newUnit =
                                                                unit;
                                                              if (
                                                                prev.weight
                                                              ) {
                                                                const convertedWeight =
                                                                  newUnit ===
                                                                  "Lbs"
                                                                    ? prev.weight *
                                                                      2.20462
                                                                    : prev.weight /
                                                                      2.20462;
                                                                return {
                                                                  ...prev,
                                                                  weightUnit:
                                                                    newUnit,
                                                                  weight:
                                                                    Math.round(
                                                                      convertedWeight
                                                                    ),
                                                                };
                                                              }
                                                              return {
                                                                ...prev,
                                                                weightUnit:
                                                                  newUnit,
                                                              };
                                                            }
                                                          );
                                                        }}
                                                        className={`px-2 py-0.5 border rounded-md mx-0.5 text-xs ${
                                                          selectedTags.weightUnit ===
                                                          unit
                                                            ? "bg-blue-500 text-white"
                                                            : "bg-white text-black border-gray-300"
                                                        }`}
                                                        type="button"
                                                        disabled={
                                                          unit ===
                                                          selectedTags?.weightUnit
                                                        }
                                                      >
                                                        {t(
                                                          `preferences.units.${unit.toLowerCase()}`
                                                        )}
                                                      </button>
                                                    )
                                                  )}
                                                </div>
                                              </InputAdornment>
                                            ),
                                          }}
                                          placeholder={`${t(
                                            `preferences.fields.weight`
                                          )} in ${t(
                                            `preferences.units.${selectedTags?.weightUnit?.toLowerCase()}`
                                          )}`}
                                        />
                                      </div>
                                    ) : key === "targetWeight" ? (
                                      <div className="w-full">
                                        <TextField
                                          type="number"
                                          variant="outlined"
                                          fullWidth
                                          size="small"
                                          className="!text-black"
                                          value={
                                            selectedTags.targetWeight || ""
                                          }
                                          onChange={(e) =>
                                            handleInputChange(
                                              "targetWeight",
                                              e.target.value,
                                              "Target Weight"
                                            )
                                          }
                                          onKeyDown={(e) => {
                                            if (
                                              ["e", "E", "+", "-"].includes(
                                                e.key
                                              )
                                            ) {
                                              e.preventDefault();
                                            }
                                          }}
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              fontSize: "0.875rem",
                                              color: "black",
                                              height: "40px",
                                              paddingRight: "5px",
                                              "& fieldset": {
                                                borderColor:
                                                  errors.targetWeight
                                                    ? "#EF4444"
                                                    : "#e5e7eb",
                                                borderRadius: "0.375rem",
                                                borderWidth: "0.5px",
                                              },
                                              "&:hover fieldset": {
                                                borderColor:
                                                  errors.targetWeight
                                                    ? "#EF4444"
                                                    : "#e5e7eb",
                                                borderWidth: "0.5px",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor:
                                                  errors.targetWeight
                                                    ? "#EF4444"
                                                    : "#e5e7eb",
                                                boxShadow: "none",
                                                outline: "none",
                                                borderWidth: "0.5px",
                                              },
                                              "& input::placeholder": {
                                                fontSize: "0.75rem",
                                                opacity: 1,
                                                color: "#9CA3AF",
                                              },
                                              "&.Mui-disabled": {
                                                color: "black !important",
                                                "& .MuiOutlinedInput-notchedOutline":
                                                  {
                                                    borderColor:
                                                      "#e5e7eb !important",
                                                  },
                                                "& input": {
                                                  color: "black !important",
                                                  backgroundColor: "#f8f8f8ff",
                                                  "-webkit-text-fill-color":
                                                    "black !important",
                                                },
                                              },
                                            },
                                          }}
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment
                                                position="end"
                                                className="!bg-[transparent]"
                                              >
                                                <div className="flex items-center mr-1">
                                                  {["kg", "Lbs"].map(
                                                    (unit) => (
                                                      <button
                                                        key={unit}
                                                        onClick={() => {
                                                          setSelectedTags(
                                                            (prev) => {
                                                              const newUnit =
                                                                unit;
                                                              if (
                                                                prev.targetWeight
                                                              ) {
                                                                const convertedWeight =
                                                                  newUnit ===
                                                                  "Lbs"
                                                                    ? prev.targetWeight *
                                                                      2.20462
                                                                    : prev.targetWeight /
                                                                      2.20462;
                                                                return {
                                                                  ...prev,
                                                                  targetWeightUnit:
                                                                    newUnit,
                                                                  targetWeight:
                                                                    Math.round(
                                                                      convertedWeight
                                                                    ),
                                                                };
                                                              }
                                                              return {
                                                                ...prev,
                                                                targetWeightUnit:
                                                                  newUnit,
                                                              };
                                                            }
                                                          );
                                                        }}
                                                        className={`px-2 py-0.5 border rounded-md mx-0.5 text-xs ${
                                                          selectedTags?.targetWeightUnit ===
                                                          unit
                                                            ? "bg-blue-500 text-white"
                                                            : "bg-white text-black border-gray-300"
                                                        } `}
                                                        type="button"
                                                        disabled={
                                                          unit ===
                                                          selectedTags?.targetWeightUnit
                                                        }
                                                      >
                                                        {t(
                                                          `preferences.units.${unit?.toLowerCase()}`
                                                        )}
                                                      </button>
                                                    )
                                                  )}
                                                </div>
                                              </InputAdornment>
                                            ),
                                          }}
                                          placeholder={`${t(
                                            `preferences.fields.targetWeight`
                                          )} in ${t(
                                            `preferences.units.${selectedTags?.targetWeightUnit?.toLowerCase()}`
                                          )}`}
                                        />
                                      </div>
                                    ) : (
                                      <input
                                        min="1"
                                        type="number"
                                        className={`w-full border p-2 rounded-md h-10 text-sm focus:outline-none ${
                                          errors[key]
                                            ? "border-red-500 text-red-500"
                                            : "text-black"
                                        } ${
                                          label ===
                                            "Maintenance Calories (Kcal)" ||
                                          label === "Target Daily Calories (Kcal)"
                                            ? "bg-gray-100 cursor-not-allowed"
                                            : "bg-white"
                                        }`}
                                        value={selectedTags[key] || ""}
                                        onChange={(e) =>
                                          handleInputChange(
                                            key,
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          label ===
                                            "Maintenance Calories (Kcal)" ||
                                          label === "Target Daily Calories (Kcal)"
                                        }
                                        onKeyDown={(e) => {
                                          if (
                                            ["e", "E", "+", "-"].includes(e.key)
                                          ) {
                                            e.preventDefault();
                                          }
                                        }}
                                        placeholder={`${t(
                                          `preferences.fields.${key}`
                                        )}`}
                                      />
                                    )}
                                    {errors[key] && (
                                      <div className="text-red-500 text-xs mt-1 ml-1">
                                        {errors[key]}
                                      </div>
                                    )}
                                  </>)
                                )}
                              </div>
                            </div>
                            {/* Selected Tags - Full width on mobile, 1 col on desktop */}
                            <div className="md:col-span-1">
                              <div className="flex flex-wrap gap-2 min-h-[40px] items-start">
                                {type !== "numeric" && (
                                  <>
                                    {selectedTags[key] ? (
                                      Array.isArray(selectedTags[key]) ? (
                                        selectedTags[key]?.map((tag, index) => (
                                          <span
                                            key={tag}
                                            className="inline-flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs max-w-full break-words"
                                            style={{ wordBreak: 'break-word' }}
                                          >
                                            <span className="flex-shrink min-w-0">
                                              {key === "selectedCountry"
                                                ? countryCodeToName[tag]
                                                : t(
                                                    `preferences.options.${key}.${tag}`,
                                                    tag
                                                  )}
                                            </span>
                                            <button
                                              onClick={() =>
                                                removeTag(
                                                  key,
                                                  selectedTags[key][index]
                                                )
                                              }
                                              className="ml-1 text-blue-500 hover:text-blue-800 flex-shrink-0"
                                            >
                                              ✕
                                            </button>
                                          </span>
                                        ))
                                      ) : (
                                        <span className="inline-flex bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs max-w-full break-words">
                                          {key !== "selectedCountry"
                                            ? t(
                                                `preferences.options.${key}.${selectedTags[key]}`,
                                                selectedTags[key]
                                              )
                                            : countryCodeToName[
                                                selectedTags[key]
                                              ]}
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-400 text-xs">
                                        {t("preferences.noOptionSelected")}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })
                  ) : (
                    <div className="text-center py-8">
                      <NotFound message="No Preferences Found." />
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end items-center gap-3 mt-6 md:mt-8">
                {isReacreate && (
                  <button
                    onClick={() => navigate("/diet-plan")}
                    className={`w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-full text-sm font-medium transition-colors hover:bg-gray-600 ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isLoading}
                  >
                    {t("preferences.buttons.cancel")}
                  </button>
                )}

                {fields?.length > 0 && (
                  <button
                    onClick={handleClickOpen}
                    className={`w-full sm:w-auto px-6 py-3 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-full text-sm font-medium transition-colors hover:from-blue-700 hover:to-blue-500 ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <ButtonLoader />
                    ) : (
                      t("preferences.buttons.confirm")
                    )}
                  </button>
                )}
              </div>
            </div>

            <AlertDialog
              open={open}
              handleClickOpen={handleClickOpen}
              handleClose={handleClose}
              isLoading={isLoading}
              heading={t("preferences.dialog.heading")}
              message={t("preferences.dialog.message")}
            />
          </>
        )}
      </>
    );
  }
};

export default PreferencesSelection;